import type { SidenavItem, SidenavSection } from "@/services/sidenavService";

/**
 * Normalizes a route path for uniform comparison (no leading slashes, trimmed, lowercase).
 */
export const normalizeRoute = (route?: string | null): string => {
  return String(route || "")
    .trim()
    .toLowerCase()
    .replace(/^\/+/, "");
};

/**
 * Checks whether an item represents or contains Settings.
 */
export const isSettingsNavItem = (item: SidenavItem | any): boolean => {
  if (!item) return false;
  if (item.id === "settings-navigation-group") return true;

  const label = String(item.label || "").trim().toLowerCase();
  if (label === "settings") return true;

  const route = normalizeRoute(item.route);
  if (
    route === "settings" ||
    route === "settings/system-apps" ||
    route === "settings/system" ||
    route === "settings/notifications"
  ) {
    return true;
  }

  if (Array.isArray(item.children) && item.children.length > 0) {
    return item.children.some((child: any) => isSettingsNavItem(child));
  }

  return false;
};

/**
 * Checks if a specific route exists anywhere in an item tree (top-level or nested children).
 */
export const hasRouteInItems = (
  items: (SidenavItem | any)[],
  targetRoute: string
): boolean => {
  const normalizedTarget = normalizeRoute(targetRoute);
  for (const item of items) {
    if (normalizeRoute(item?.route) === normalizedTarget) {
      return true;
    }
    if (Array.isArray(item?.children) && item.children.length > 0) {
      if (hasRouteInItems(item.children, targetRoute)) {
        return true;
      }
    }
  }
  return false;
};

/**
 * Builds a hierarchical navigation tree from a flat or partially-nested list of SidenavItems.
 * Correctly maps child sub-tabs to their parent items based on parent_id,
 * preserves existing children without duplication, and sorts both levels by order.
 */
export const buildNavigationTree = (
  rawItems: (SidenavItem | any)[]
): (SidenavItem | any)[] => {
  if (!Array.isArray(rawItems) || rawItems.length === 0) return [];

  const subTabs = rawItems.filter(
    (item) => item.parent_id !== null && item.parent_id !== undefined
  );
  const topLevel = rawItems.filter(
    (item) => item.parent_id === null || item.parent_id === undefined
  );

  const childMap = new Map<number | string, (SidenavItem | any)[]>();
  for (const child of subTabs) {
    const pid = child.parent_id;
    if (!childMap.has(pid)) {
      childMap.set(pid, []);
    }
    childMap.get(pid)!.push(child);
  }

  const result: (SidenavItem | any)[] = [];

  for (const parent of topLevel) {
    const directChildren = childMap.get(parent.id) || [];
    const existingChildren = Array.isArray(parent.children)
      ? [...parent.children]
      : [];

    const existingChildIds = new Set(
      existingChildren.map((c) => String(c.id ?? c.route))
    );

    const mergedChildren = [...existingChildren];
    for (const child of directChildren) {
      const childKey = String(child.id ?? child.route);
      if (!existingChildIds.has(childKey)) {
        mergedChildren.push(child);
        existingChildIds.add(childKey);
      }
    }

    // Sort children by display order
    mergedChildren.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    result.push({
      ...parent,
      children: mergedChildren,
    });
  }

  // Sort top-level items by display order
  result.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return result;
};

/**
 * Normalizes all sections by applying the unified tree-building logic to their items.
 */
export const normalizeSectionsWithTree = (
  sections: SidenavSection[]
): SidenavSection[] => {
  if (!Array.isArray(sections)) return [];

  return sections.map((sec) => ({
    ...sec,
    items: buildNavigationTree(sec.items || []),
  }));
};
